import {
    JSONAPI,
    FSMNamedState,
    ObservableFiniteStateMachine,
    OMReference,
    OMUniverse
} from "@apptivity-lab/firmament-node-sdk"
import { AffAttendeeProfile, AffExpertProfile } from "@firmament-packages/aff"
import AffRating from "@firmament-packages/aff/AffRating"

import { CnxBooking, CnxConsumerProfile, CnxConsumerPurchase, CnxMeeting, CnxSessionWallet } from "@firmament-packages/connectx"
import { WltWallet } from "@firmament-packages/wallet"

declare namespace CnxMeetingDetailStateMachine {
    export interface Start extends FSMNamedState {
        name: "start"
    }

    export interface Loading extends FSMNamedState {
        name: "loading"
        meetingId: OMReference<CnxMeeting>
    }

    export interface Presenting extends FSMNamedState {
        name: "presenting"
        meeting: CnxMeeting
    }

    export interface PerformingBooking extends FSMNamedState {
        name: "performingBooking"
        consumer: CnxConsumerProfile,
        meeting: CnxMeeting
    }

    export interface ShowingMeetingBooked extends FSMNamedState {
        name: "showingMeetingBooked"
        meeting: CnxMeeting
        booking: CnxBooking
    }

    export interface ShowingInsufficientBalance extends FSMNamedState {
        name: "showingInsufficientBalance"
        meeting: CnxMeeting
    }

    export interface ShowingBookingError extends FSMNamedState {
        name: "showingBookingError"
        error: Error
        consumer: CnxConsumerProfile
        meeting: CnxMeeting
    }

    export interface PerformingBookingCancellation extends FSMNamedState {
        name: "performingBookingCancellation"
        consumer: CnxConsumerProfile
        meeting: CnxMeeting
    }

    export interface ShowingRatingForm extends FSMNamedState {
        name: "showingRatingForm"
        consumer: CnxConsumerProfile
        meeting: CnxMeeting
    }

    export interface SavingRating extends FSMNamedState {
        name: "savingRating"
        rating: AffRating
        consumer: CnxConsumerProfile
        meeting: CnxMeeting
    }

    export interface ShowingRatingError extends FSMNamedState {
        name: "showingRatingError"
        error: Error
        rating: AffRating,
        consumer: CnxConsumerProfile
        meeting: CnxMeeting
    }

    export interface ShowingError extends FSMNamedState {
        name: "showingError"
        error: Error
        meeting?: CnxMeeting
    }

    export type State = Start |
        Loading | Presenting |
        PerformingBooking | ShowingMeetingBooked | ShowingInsufficientBalance | ShowingBookingError |
        PerformingBookingCancellation |
        ShowingRatingForm | SavingRating | ShowingRatingError |
        ShowingError
}

export class CnxMeetingDetailStateMachine extends ObservableFiniteStateMachine<CnxMeetingDetailStateMachine.State> {
    private meetingId?: OMReference<CnxMeeting>

    constructor({ meeting }: { meeting: JSONAPI }) {
        const cachedMeetings = OMUniverse.shared.apiClient.objectsFromResponse(CnxMeeting, meeting)
        if (cachedMeetings.length > 0) {
            super({ name: "presenting", meeting: cachedMeetings[0] })
            this.meetingId = new OMReference(CnxMeeting, cachedMeetings[0])
        } else {
            super({ name: "start" })
        }
    }

    async load(id: OMReference<CnxMeeting>) {
        switch (this.state.name) {
            case "start":
            case "presenting":
            case "showingMeetingBooked":
            case "performingBookingCancellation":
            case "savingRating":
                break
            default:
                return
        }

        this.state = {
            name: "loading",
            meetingId: id
        }

        this.meetingId = id

        try {
            const meeting = await id.load(
                CnxMeeting.query()
                    .include("serviceTier")
                    .include("meetingTemplate")
                    .include("videoRoom")
                    .include("bookings")
                    .include("coverPhoto")
                    .include("categories")
                    .include("provider")
                    .include("provider.photo" as any)
            )

            this.success(meeting)
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            }
        }
    }

    async book(profile: AffAttendeeProfile, meeting: CnxMeeting) {
        switch (this.state.name) {
            case "presenting":
                break
            default:
                return
        }

        this.state = {
            name: "performingBooking",
            consumer: profile,
            meeting
        }

        try {
            // Ensure we have the latest wallet values by loading wallet again
            const wallets = await CnxSessionWallet.query()
                .filter("consumer", "equals", profile.id as any)
                .execute()
                .then((query) => query.resultObjects)

            const wallet = wallets[0]
            profile.sessionWallets = [new OMReference(CnxSessionWallet, wallet)]
            OMUniverse.shared.touch(profile, true)

            // Ensure profile have enough balance
            if (wallet.transactionsAvailableBalance < meeting.servicePriceCents()) {
                this.state = {
                    name: "showingInsufficientBalance",
                    meeting
                }
                return
            }

            // Record the purchase transaction
            const purchase = new CnxConsumerPurchase({
                meeting: new OMReference(CnxMeeting, meeting),
                wallet: new OMReference(WltWallet, wallet),
                transactionType: "CREDIT",
                transactionStatus: "APPROVED",
                currency: "SSN",
                amountInCents: meeting.servicePriceCents()
            })
            OMUniverse.shared.touch(purchase)
            await purchase.save()

            // Create booking for consumer for this meeting
            const booking = new CnxBooking({
                consumer: new OMReference(CnxConsumerProfile, profile),
                meeting: new OMReference(CnxMeeting, meeting),
                bookingStatus: "PENDING",
                purchase: new OMReference(CnxConsumerPurchase, purchase),
                paymentCurrency: wallet.currency,
                paymentGrossRateCents: purchase.amountInCents
            })
            OMUniverse.shared.touch(booking)
            await booking.save()

            // Confirm the booking
            await booking.confirm()

            this.state = {
                name: "showingMeetingBooked",
                meeting,
                booking
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            } else {
                this.fail(new Error("An unknown error has occurred while attempting to place booking."))
            }
        }
    }

    showRatingForm(submitter: AffAttendeeProfile) {
        switch (this.state.name) {
            case "presenting":
                break
            default:
                return
        }

        this.state = {
            name: "showingRatingForm",
            consumer: submitter,
            meeting: this.state.meeting
        }
    }

    async saveRating(rating: AffRating, submitter: AffAttendeeProfile) {
        switch (this.state.name) {
            case "showingRatingForm":
            case "showingRatingError":
                break
            default:
                return
        }

        try {
            this.state = {
                name: "savingRating",
                rating,
                consumer: submitter,
                meeting: this.state.meeting
            }

            if (this.state.meeting.meetingTemplate) {
                rating.meetingTemplate = this.state.meeting.meetingTemplate
            }
            if (this.state.meeting.provider) {
                rating.provider = new OMReference(AffExpertProfile, this.state.meeting.provider.id)
            }

            OMUniverse.shared.touch(rating)
            console.log(rating)
            await rating.save()

            this.success(this.state.meeting)
        } catch (error: unknown) {
            console.log(error)
            if (error instanceof Error) {
                this.fail(error)
            } else {
                this.fail(new Error("An unknown error has occurred while attempting to save your review."))
            }
        }
    }

    async cancelBooking(profile: AffAttendeeProfile, meeting: CnxMeeting, booking: CnxBooking) {
        switch (this.state.name) {
            case "presenting":
            case "showingMeetingBooked":
                break
            default:
                return
        }

        this.state = {
            name: "performingBookingCancellation",
            consumer: profile,
            meeting
        }

        try {
            await booking.cancel()
            this.success(meeting)
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            } else {
                this.fail(new Error("An unknown error has occurred while attempting to cancel booking."))
            }
        }
    }

    back() {
        switch (this.state.name) {
            case "showingMeetingBooked":
            case "showingInsufficientBalance":
            case "showingBookingError":
                const meeting = this.meetingId?.actualObject
                this.state = { name: "presenting", meeting: meeting || this.state.meeting }
                break
            case "showingRatingForm":
            case "showingRatingError":
                this.state = { name: "presenting", meeting: meeting || this.state.meeting }
                break
            default:
                return
        }
    }

    private success(meeting: CnxMeeting) {
        switch (this.state.name) {
            case "loading":
                this.state = { name: "presenting", meeting }
                break
            case "performingBookingCancellation":
            case "savingRating":
                this.load(new OMReference(CnxMeeting, meeting))
                break
            default:
                return
        }
    }

    private fail(error: Error) {
        switch (this.state.name) {
            case "loading":
                this.state = { name: "showingError", error, meeting: this.state.meetingId.actualObject }
                break
            case "performingBooking":
            case "performingBookingCancellation":
                this.state = {
                    name: "showingBookingError",
                    error,
                    consumer: this.state.consumer,
                    meeting: this.state.meeting
                }
                break
            case "savingRating":
                this.state = {
                    name: "showingRatingError",
                    error,
                    rating: this.state.rating,
                    consumer: this.state.consumer,
                    meeting: this.state.meeting
                }
                break
            default:
                return
        }
    }
}
